import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NotificationEngineService from '../../Admin/services/NotificationEngineService';

const NOTIFICATION_STORAGE_KEY = 'nf_workspace_notification_prefs_v1';

const card = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,.08)',
  padding: 28,
};

function loadNotificationPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!raw) return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
    const o = JSON.parse(raw);
    return {
      dueFollowUpAlerts: o.dueFollowUpAlerts !== false,
      overdueBadge: o.overdueBadge !== false,
      soundHints: !!o.soundHints,
    };
  } catch {
    return { dueFollowUpAlerts: true, overdueBadge: true, soundHints: false };
  }
}

export default function SuperAdminPlatformSettings() {
  const [toast, setToast] = useState(null);
  const [notif, setNotif] = useState(loadNotificationPrefs);
  const [platform, setPlatform] = useState({
    notificationWorkerEnabled: true,
    leadSchedulerEnabled: true,
  });
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformLoading, setPlatformLoading] = useState(true);

  const showMsg = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadPlatformSettings = useCallback(async () => {
    setPlatformLoading(true);
    try {
      const data = await NotificationEngineService.getPlatformSettings();
      setPlatform({
        notificationWorkerEnabled: !!(data?.notificationWorkerEnabled ?? data?.NotificationWorkerEnabled ?? true),
        leadSchedulerEnabled: !!(data?.leadSchedulerEnabled ?? data?.LeadSchedulerEnabled ?? true),
      });
    } catch {
      showMsg('Could not load platform settings.', 'error');
    } finally {
      setPlatformLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlatformSettings();
  }, [loadPlatformSettings]);

  const savePlatformSettings = async () => {
    setPlatformSaving(true);
    try {
      await NotificationEngineService.updatePlatformSettings({
        notificationWorkerEnabled: platform.notificationWorkerEnabled,
        leadSchedulerEnabled: platform.leadSchedulerEnabled,
      });
      showMsg('Platform settings saved.');
      loadPlatformSettings();
    } catch {
      showMsg('Could not save platform settings.', 'error');
    } finally {
      setPlatformSaving(false);
    }
  };

  const persistNotif = (next) => {
    setNotif(next);
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(next));
    showMsg('Display preferences saved on this device.');
  };

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'error' ? '#f5365c' : '#2dce89',
            color: '#fff',
            padding: '14px 28px',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,.2)',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-globe" style={{ color: '#5e72e4', marginRight: 10 }} />
          Platform settings
        </h3>
        <p style={{ margin: '8px 0 0', color: '#8898aa', fontSize: 14, maxWidth: 720 }}>
          Global switches for all agencies. Per-agency attendance area and lead automation are configured under{' '}
          <Link to="/client" style={{ color: '#5e72e4', fontWeight: 700 }}>Agencies → Edit client</Link>.
        </p>
      </div>

      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'linear-gradient(135deg,#11cdef,#5e72e4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            <i className="fas fa-cogs" />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#344767' }}>Platform automation</h4>
            <p style={{ margin: '6px 0 0', color: '#8898aa', fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
              Applies to every agency on the platform. Requires the NotificationWorker process to be running on the server.
            </p>
          </div>
        </div>

        {platformLoading ? (
          <p style={{ color: '#8392ab', textAlign: 'center', padding: 24 }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
            Loading…
          </p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '14px 16px', borderRadius: 12, border: '1px solid #eef1f6', background: '#fafbfe' }}>
                <input
                  type="checkbox"
                  checked={platform.notificationWorkerEnabled}
                  onChange={(e) => setPlatform((p) => ({ ...p, notificationWorkerEnabled: e.target.checked }))}
                />
                <span>
                  <span style={{ fontWeight: 700, color: '#344767', display: 'block' }}>Notification worker</span>
                  <span style={{ fontSize: 12, color: '#8898aa' }}>Process notification queue for all clients</span>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '14px 16px', borderRadius: 12, border: '1px solid #eef1f6', background: '#fafbfe' }}>
                <input
                  type="checkbox"
                  checked={platform.leadSchedulerEnabled}
                  onChange={(e) => setPlatform((p) => ({ ...p, leadSchedulerEnabled: e.target.checked }))}
                />
                <span>
                  <span style={{ fontWeight: 700, color: '#344767', display: 'block' }}>Lead schedulers</span>
                  <span style={{ fontSize: 12, color: '#8898aa' }}>Follow-ups, escalation, inactivity re-assign</span>
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={platformSaving}
                onClick={savePlatformSettings}
                style={{
                  padding: '12px 28px',
                  borderRadius: 10,
                  border: 'none',
                  background: platformSaving ? '#adb5bd' : 'linear-gradient(135deg,#11cdef,#5e72e4)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: platformSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {platformSaving ? 'Saving…' : 'Save platform settings'}
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ ...card, marginBottom: 20 }}>
        <h4 style={{ marginTop: 0, color: '#344767' }}>
          <i className="fas fa-building" style={{ color: '#5e72e4', marginRight: 8 }} />
          Per-agency configuration
        </h4>
        <p style={{ color: '#8898aa', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
          Each agency can have a different office location for attendance check-in and its own Meta lead automation rules.
          Open the agency from the list below to manage these settings.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <div style={{ padding: 16, borderRadius: 12, border: '1px solid #eef1f6', background: '#fafbfe' }}>
            <div style={{ fontWeight: 800, color: '#344767', marginBottom: 6 }}>
              <i className="fas fa-map-marker-alt" style={{ color: '#2dce89', marginRight: 8 }} />
              Attendance area
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#8898aa' }}>Geo-fence lat/lng/radius per agency</p>
          </div>
          <div style={{ padding: 16, borderRadius: 12, border: '1px solid #eef1f6', background: '#fafbfe' }}>
            <div style={{ fontWeight: 800, color: '#344767', marginBottom: 6 }}>
              <i className="fas fa-clock" style={{ color: '#5e72e4', marginRight: 8 }} />
              Lead automation
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#8898aa' }}>Meta auto-assign &amp; inactivity re-assign</p>
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <Link
            to="/client"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '11px 20px',
              borderRadius: 10,
              background: 'linear-gradient(135deg,#5e72e4,#825ee4)',
              color: '#fff',
              fontWeight: 700,
              textDecoration: 'none',
              fontSize: 14,
            }}
          >
            <i className="fas fa-arrow-right" />
            Manage agencies
          </Link>
        </div>
      </div>

      <div style={card}>
        <h4 style={{ marginTop: 0, color: '#344767' }}>Display preferences</h4>
        <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 20 }}>
          Stored on this browser only (local storage).
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={notif.dueFollowUpAlerts}
            onChange={(e) => persistNotif({ ...notif, dueFollowUpAlerts: e.target.checked })}
          />
          <span style={{ fontWeight: 600, color: '#344767' }}>Highlight due follow-ups in navigation</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={notif.overdueBadge}
            onChange={(e) => persistNotif({ ...notif, overdueBadge: e.target.checked })}
          />
          <span style={{ fontWeight: 600, color: '#344767' }}>Show overdue counts on the menu badge</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="checkbox"
            checked={notif.soundHints}
            onChange={(e) => persistNotif({ ...notif, soundHints: e.target.checked })}
          />
          <span style={{ fontWeight: 600, color: '#344767' }}>Allow subtle UI sounds for confirmations</span>
        </label>
      </div>
    </div>
  );
}
